import Header from "./partials/Header";
import Layout from "@/containers/Layout";
import MentionsOverTime from "./partials/MentionsOverTime";
import { getColorBySearchId } from "@/utils/color";
import { getGreeting } from "@/lib/greeting";
import { getNewDashboardData } from "@/api/endpoints/dashboard";
import { i18n } from "@lingui/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Separator } from "@components/ui/separator";
import { Badge } from "@components/ui/badge";
import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon, SunIcon } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "@components/ui/carousel";
import { SearchLight } from "@/types/Search";
import MentionCardWithLoader from "@/containers/MentionCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUser } from "@/hooks/useUser";
import Error from "@components/ui/error";
import { Skeleton } from "@components/ui/skeleton";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import MentionCardsProvider from "@/containers/MentionCardsProvider";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import ShareModal from "@/containers/ShareModal";
import { sub } from "date-fns";

const MAX_SEARCHES_DISPLAY_COUNT = 5;

function Search({ search }: { search: SearchLight }) {
  const { t } = useLingui();

  // Carousel state
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } =
    useCarousel();

  // Mobile state
  const isMobile = useIsMobile();

  // Global state
  const { mentionCards, loading, errors } = useMentionCards();

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{search.title}</h2>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="icon"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          >
            <ChevronLeftIcon />
            <span className="sr-only">
              <Trans>Föregående</Trans>
            </span>
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={scrollNext}
            disabled={!canScrollNext}
          >
            <ChevronRightIcon />
            <span className="sr-only">
              <Trans>Nästa</Trans>
            </span>
          </Button>
        </div>
      </div>

      {loading ? (
        <Skeleton className="w-full h-56 rounded-md" />
      ) : errors?.length ? (
        <Error
          withBorder
          heading={t`Åh nej!`}
          description={t`Något gick snett med att ladda omnämnanden. Vänligen försök igen senare`}
        />
      ) : mentionCards.length ? (
        <CarouselContent>
          {mentionCards.map((mentionCard) => (
            <CarouselItem key={mentionCard.episode.id}>
              <MentionCardWithLoader {...mentionCard} isMobile={isMobile} />
            </CarouselItem>
          ))}
        </CarouselContent>
      ) : (
        <Empty withBorder>
          <EmptyHeader>
            <EmptyMedia>
              <SunIcon />
            </EmptyMedia>
            <EmptyTitle>
              <Trans>Inget att ta ikapp</Trans>
            </EmptyTitle>
            <EmptyDescription>
              <Trans>
                Det finns inga nya omnämnanden för den här bevakningen under de
                senaste 24 timmarna.
              </Trans>
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      <ShareModal v4 />
    </>
  );
}

export default function HomeView() {
  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Local state
  const [selectedSearchIds, setSelectedSearchIds] = useState<string[]>([]);

  // Server state
  const { data: userData } = useUser();
  const user = userData?.ok ? userData.data : undefined;

  const { data: dashboardData } = useQuery({
    queryKey: [orgUuid, "dashboard"],
    queryFn: () => getNewDashboardData(MAX_SEARCHES_DISPLAY_COUNT),
    refetchOnWindowFocus: false,
  });

  const totalMentionsOverTime = dashboardData?.totalMentionsOverTime;
  const initialSearchIds = dashboardData?.initialSearchIds;
  const searches = dashboardData?.searches;

  // Side-effects
  useEffect(() => {
    if (initialSearchIds && selectedSearchIds.length === 0) {
      setSelectedSearchIds(initialSearchIds);
    }
  }, [initialSearchIds, selectedSearchIds]);

  // Helpers
  const hasChangedSearchIds = useMemo(() => {
    return !(
      selectedSearchIds.length === initialSearchIds?.length &&
      initialSearchIds.every((searchId) => selectedSearchIds.includes(searchId))
    );
  }, [selectedSearchIds, initialSearchIds]);

  // Handlers
  function handleSelectSearchId(searchId: string) {
    setSelectedSearchIds((prev) => [...prev, searchId]);
  }

  function handleDeselectSearchId(searchId: string) {
    setSelectedSearchIds((prev) => prev.filter((id) => id !== searchId));
  }

  function handleGetSearchColor(searchId: string) {
    return getColorBySearchId(
      searchId,
      searches!.map((search) => search.uuid)
    );
  }

  const mentionCardsStartDate = useMemo(() => {
    return sub(new Date(), { hours: 24 });
  }, []);

  return (
    <Layout>
      <Header />

      <div className="border-b">
        <div className="w-full p-4 lg:p-6 max-w-7xl">
          {user && (
            <h1 className="text-2xl font-bold">
              {i18n._(getGreeting(user?.name))} 👋
            </h1>
          )}
        </div>
      </div>

      <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-8">
        {totalMentionsOverTime && searches && (
          <MentionsOverTime
            hasChangedSearchIds={hasChangedSearchIds}
            selectedSearchIds={selectedSearchIds}
            onSelectSearchId={handleSelectSearchId}
            onDeselectSearchId={handleDeselectSearchId}
            getSearchColor={handleGetSearchColor}
            chartData={totalMentionsOverTime}
            searches={searches}
          />
        )}

        <div className="flex items-center gap-2">
          <Separator className="flex-1" />
          <Badge
            variant="outline"
            className="rounded-md px-1.5 py-1 text-muted-foreground"
          >
            <Trans>Senaste 24 timmarna</Trans>
          </Badge>
          <Separator className="flex-1" />
        </div>

        {searches && searches.length > 0 && (
          <div className="flex flex-col gap-8">
            {searches.map((search) => (
              <Carousel key={search.uuid} className="flex flex-col gap-5">
                <MentionCardsProvider
                  searchUuid={search.uuid}
                  startDate={mentionCardsStartDate}
                >
                  <Search search={search} />
                </MentionCardsProvider>
              </Carousel>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
